import { useState, useRef, useEffect, useCallback } from 'react';
import { useOnChange, usePageParam, useResetPageParams } from '../../hooks/usePageParam';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import DashboardLayout from '../../components/layout/DashboardLayout';
import FileUploadZone, { extensionLabel } from '../../components/common/FileUploadZone';
import ConversionProgressBar from '../../components/common/ConversionProgressBar';
import Modal from '../../components/common/Modal';
import { useConversionProgress } from '../../hooks/useConversionProgress';
import { useAuth } from '../../contexts/AuthContext';
import { PROJECT_QUERY, PROJECT_FILES_QUERY, FILE_FOLDERS_QUERY, GAUSSIAN_QUOTA_QUERY } from '../../graphql/queries';
import { truncateName } from '../../utils/text';
import {
  DELETE_FILE_MUTATION,
  CREATE_FILE_FOLDER_MUTATION,
  RENAME_FILE_FOLDER_MUTATION,
  DELETE_FILE_FOLDER_MUTATION,
  SET_FILE_FOLDER_MUTATION,
  GENERATE_GAUSSIAN_SPLAT_MUTATION,
} from '../../graphql/mutations';
import { accessOf } from '../../utils/access';
import styles from './FileListPage.module.css';
import { isSuperAdmin } from '../../utils/staff';
import { canEditProject, projectRoleFor } from '../../utils/projectRole';
import { useDocumentFormats } from '../../hooks/useDocumentFormats';
import { documentOpensIn, documentViewerRoute, type FileFormat } from '../../utils/fileFormats';
import { downloadProjectFile } from '../../utils/fileDownload';
import Icon, { type IconName } from '../../components/common/Icon';

type FilePageType = '2d' | '3d' | 'documents' | 'point-cloud' | 'gaussian' | 'orthomosaic' | 'video360';

export const PAGE_CONFIG: Record<FilePageType, { title: string; icon: IconName; fileType: string; allowedExtensions: string[]; viewerPath: string; color: string }> = {
  '2d': { title: '2D Drawings', icon: 'architecture', fileType: 'two_d', allowedExtensions: ['dwg', 'cad', 'dxf'], viewerPath: 'viewer-2d', color: 'bg-blue-50 text-blue-600' },
  '3d': { title: '3D BIM Models', icon: 'view_in_ar', fileType: 'three_d', allowedExtensions: ['ifc', 'rvt', 'rcs', 'obj', 'stl', 'gltf', 'glb', 'zip'], viewerPath: 'viewer-3d-v2', color: 'bg-purple-50 text-purple-600' },
  'documents': { title: 'Documents', icon: 'description', fileType: 'document', allowedExtensions: ['pdf'], viewerPath: 'viewer-pdf', color: 'bg-red-50 text-red-600' },
  'point-cloud': { title: 'Point Cloud', icon: 'grain', fileType: 'point_cloud', allowedExtensions: ['e57', 'las', 'laz', 'zip', 'lgsx'], viewerPath: 'viewer-cloud', color: 'bg-orange-50 text-orange-600' },
  'gaussian': { title: 'Gaussian Splats', icon: 'blur_on', fileType: 'gaussian', allowedExtensions: ['ply', 'e57', 'zip'], viewerPath: 'viewer-splat', color: 'bg-indigo-50 text-indigo-600' },
  'orthomosaic': { title: 'Orthomosaic', icon: 'satellite_alt', fileType: 'orthomosaic', allowedExtensions: ['tif', 'tiff'], viewerPath: 'viewer-ortho', color: 'bg-sky-50 text-sky-600' },
  'video360': { title: '360 Videos', icon: 'panorama_photosphere', fileType: 'video360', allowedExtensions: ['mp4', 'mov'], viewerPath: 'viewer-360', color: 'bg-amber-50 text-amber-700' },
};

interface PCFolder { id: string; name: string; createdAt: string; }
interface PCFile {
  id: string;
  originalName: string;
  extension: string;
  fileSize: number;
  status: string;
  createdAt: string;
  folderId?: string | null;
  thumbnailUrl?: string | null;
  archiveState?: 'raw_archived' | 'archived' | 'restoring' | 'restored' | null;
  restoreScope?: 'raw' | 'full' | null;
  restoreExpiresAt?: string | null;
  uploadedBy: { id: string; firstName: string; lastName: string };
}
type PageConfig = typeof PAGE_CONFIG[FilePageType];

const formatStorage = (bytes: number): string => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

const PAGE_LIMIT = 20;

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;


// ─── File table columns ──────────────────────────────────────────────────────
// The whole file table (header here + rows in FileSection) is one <table>. It
// always fits the page: File Name takes what the fixed-width columns leave and
// cuts a long name short (a click shows it whole); on a narrower desktop the
// least needed columns drop out (FileListPage.module.css), and only phones
// scroll sideways.
const FILE_COLUMNS = [
  { key: 'name',       label: 'File Name',   cell: '' },
  { key: 'extension',  label: 'Extension',   cell: styles.colExtension },
  { key: 'size',       label: 'Size',        cell: styles.colSize },
  { key: 'uploadedBy', label: 'Uploaded By', cell: styles.colUploadedBy },
  { key: 'date',       label: 'Date',        cell: styles.colDate },
  { key: 'status',     label: 'Status',      cell: styles.colStatus },
  { key: 'actions',    label: 'Actions',     cell: styles.colActions },
] as const;

// ─── Pagination inside a <tbody> row ─────────────────────────────────────────
const PaginationRow = ({ page, total, onPageChange }: { page: number; total: number; onPageChange: (p: number) => void }) => {
  const totalPages = Math.ceil(total / PAGE_LIMIT);
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
    .reduce<(number | '...')[]>((acc, n, i, arr) => {
      if (i > 0 && n - (arr[i - 1] as number) > 1) acc.push('...');
      acc.push(n);
      return acc;
    }, []);

  return (
    <tr>
      <td colSpan={7}>
        <div className={styles.sectionPagination}>
          <span className={styles.sectionPaginationCount}>
            {(page - 1) * PAGE_LIMIT + 1}–{Math.min(page * PAGE_LIMIT, total)} of {total}
          </span>
          <div className={styles.sectionPaginationBtns}>
            <button
              onClick={() => onPageChange(Math.max(1, page - 1))}
              aria-label="Previous page"
              disabled={page === 1}
              className="p-1 rounded hover:bg-surface-container disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <Icon name="chevron_left" className="text-base" />
            </button>
            {pages.map((n, i) =>
              n === '...' ? (
                <span key={`e-${i}`} className="px-1 text-xs text-on-surface-variant">…</span>
              ) : (
                <button
                  key={n}
                  onClick={() => onPageChange(n as number)}
                  className={`min-w-[26px] h-6 px-1 text-xs rounded transition-colors ${
                    page === n ? 'bg-primary text-white font-semibold' : 'hover:bg-surface-container text-on-surface-variant'
                  }`}
                >
                  {n}
                </button>
              )
            )}
            <button
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              aria-label="Next page"
              disabled={page >= totalPages}
              className="p-1 rounded hover:bg-surface-container disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <Icon name="chevron_right" className="text-base" />
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
};

// ─── FileSection ──────────────────────────────────────────────────────────────
interface FileSectionProps {
  projectId: string;
  fileType: string;
  search: string;
  folderId: string | null | undefined;
  folders: PCFolder[];
  config: PageConfig;
  documentFormats: FileFormat[];
  folderInfo: PCFolder | null;
  showHeader: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onRenameFolder?: () => void;
  onDeleteFolder?: () => void;
  selectedFiles: Set<string>;
  onToggleSelect: (id: string) => void;
  onDeleteFile: (id: string) => void;
  onMutationComplete: () => void;
  navigate: (path: string) => void;
  downloadsExceeded?: boolean;
  onRetryGaussian?: (fileId: string) => void;
  onRequestRestore?: (file: PCFile) => void;
  onArchivedFlag?: (sectionKey: string, hasArchived: boolean) => void;
  canWrite: boolean;
  onActionError: (message: string) => void;
}

const FileSection = ({
  projectId,
  fileType,
  search,
  folderId,
  folders,
  config,
  documentFormats,
  folderInfo,
  showHeader,
  collapsed,
  onToggleCollapse,
  onRenameFolder,
  onDeleteFolder,
  selectedFiles,
  onToggleSelect,
  onDeleteFile,
  onMutationComplete,
  navigate,
  downloadsExceeded = false,
  onRetryGaussian,
  onRequestRestore,
  onArchivedFlag,
  canWrite,
  onActionError,
}: FileSectionProps) => {
  // Each section pages on its own: ?page= without folders, ?page-ungrouped= and ?page-<folderId>= with them.
  const [page, setPage] = usePageParam(folderId === undefined ? 'page' : `page-${folderId ?? 'ungrouped'}`);
  const [folderMenuFileId, setFolderMenuFileId] = useState<string | null>(null);
  const [fullNames, setFullNames] = useState<Set<string>>(() => new Set());
  const toggleFullName = (id: string) => setFullNames((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const folderMenuRef = useRef<HTMLDivElement>(null);
  const [setFileFolderMutation] = useMutation(SET_FILE_FOLDER_MUTATION);

  const handleDownload = downloadProjectFile;
  // A document opens where File Formats say (none for a download-only one);
  // RCS has no self-hosted pipeline and still opens in the Autodesk viewer.
  const viewerPathFor = (file: { extension?: string | null }): string | null => {
    const fext = (file.extension || '').toLowerCase();
    if (fileType === 'document') return documentViewerRoute(documentOpensIn(fext, documentFormats));
    return config.viewerPath === 'viewer-3d-v2' && fext === 'rcs' ? 'viewer-3d' : config.viewerPath;
  };

  const { data, loading, error, startPolling, stopPolling } = useQuery(PROJECT_FILES_QUERY, {
    variables: {
      projectId,
      type: fileType,
      search: search || undefined,
      page,
      limit: PAGE_LIMIT,
      folderId,
    },
    skip: !projectId,
    fetchPolicy: 'cache-and-network',
  });

  // While any listed file is converting, poll so status chips flip to
  // ready/error without a manual reload (3D meshing now reports back too).
  const anyProcessing = (data?.projectFiles?.files ?? []).some(
    (f: { status: string }) => f.status === 'processing',
  );
  useEffect(() => {
    if (!anyProcessing) { stopPolling(); return; }
    startPolling(8000);
    return () => stopPolling();
  }, [anyProcessing, startPolling, stopPolling]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (folderMenuRef.current && !folderMenuRef.current.contains(e.target as Node)) {
        setFolderMenuFileId(null);
      }
    };
    if (folderMenuFileId) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [folderMenuFileId]);

  const assignToFolder = async (fileId: string, targetFolderId: string | null) => {
    setFolderMenuFileId(null);
    try {
      await setFileFolderMutation({ variables: { fileId, folderId: targetFolderId } });
      onMutationComplete();
    } catch (err) {
      onActionError(`Could not move the file: ${errorMessage(err, 'unknown error')}`);
    }
  };

  const files: PCFile[] = data?.projectFiles?.files ?? [];
  const total = data?.projectFiles?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_LIMIT));
  // A ?page= past the end (an old link, files gone since) moves to the last page.
  useEffect(() => {
    if (data && page > lastPage) setPage(lastPage);
  }, [data, page, lastPage, setPage]);

  // Surface "this section shows archived files" so the page can offer the
  // project-level Unarchive button. Keyed per section (folder) so folders
  // toggling in and out don't fight each other.
  const sectionKey = folderInfo?.id ?? '__ungrouped__';
  useEffect(() => {
    onArchivedFlag?.(sectionKey, files.some((f) => f.archiveState === 'archived'));
  }, [files, sectionKey, onArchivedFlag]);

  // Point clouds, 3D meshing, splats and 360 videos report conversionProgress —
  // the status endpoint serves it for all of them (2D/documents have no progress source).
  const processingIds = fileType === 'point_cloud' || fileType === 'three_d' || fileType === 'gaussian' || fileType === 'video360'
    ? files.filter(f => f.status === 'processing').map(f => f.id)
    : [];
  const progressMap = useConversionProgress(processingIds, onMutationComplete);

  // Two-stage archive: 'raw_archived' (stage 1) keeps viewing alive — only the
  // original went cold — except for documents, whose viewer reads the original.
  // 'archived' (stage 2) blocks everything until an unarchive restores it.
  const archiveFlags = (file: PCFile) => {
    const isDocument = fileType === 'document';
    const st = file.archiveState;
    const canView = !st
      || st === 'restored'
      || ((st === 'raw_archived' || (st === 'restoring' && file.restoreScope === 'raw')) && !isDocument);
    const canDownload = !st || st === 'restored';
    const canRequest = st === 'archived' || (st === 'raw_archived' && !!onRequestRestore);
    const chip = st === 'restoring' ? 'Retrieving…'
      : st === 'restored' ? 'Retrieved'
      : st === 'archived' || (st === 'raw_archived' && isDocument) ? 'Archived'
      : null;
    const chipTitle = st === 'restored'
      ? `Retrieved from the archive — available until ${file.restoreExpiresAt ? new Date(file.restoreExpiresAt).toLocaleDateString() : 'it lapses'}`
      : st === 'restoring'
        ? 'Being retrieved from the archive (usually 3–5 hours)'
        : st === 'archived'
          ? 'Fully archived — unarchive it to view or download'
          : 'In cold storage — request the file to view or download it';
    return { canView, canDownload, canRequest, chip, chipTitle, st };
  };

  const renderFileRow = (file: PCFile) => {
    const arch = archiveFlags(file);
    return (
    <tr key={file.id} className={`table-row group ${selectedFiles.has(file.id) ? styles.rowSelected : ''}`}>
      <td className="px-6 py-4">
        <div className="flex items-center gap-3" style={folderInfo ? { paddingLeft: 20 } : undefined}>
          {canWrite && (
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={selectedFiles.has(file.id)}
              onChange={() => onToggleSelect(file.id)}
              onClick={e => e.stopPropagation()}
            />
          )}
          <div className={`w-8 h-8 rounded flex-shrink-0 overflow-hidden flex items-center justify-center text-xs font-bold ${config.color}`}>
            {file.thumbnailUrl
              ? <img src={file.thumbnailUrl} alt="" className="w-full h-full object-cover rounded" />
              : (file.extension.toLowerCase() === 'zip' ? (fileType === 'three_d' ? 'OBJ' : 'RCP') : file.extension.toUpperCase())
            }
          </div>
          <button
            type="button"
            className={`${styles.fileName} ${fullNames.has(file.id) ? styles.fileNameFull : ''}`}
            title={fullNames.has(file.id) ? 'Show less' : file.originalName}
            aria-expanded={fullNames.has(file.id)}
            onClick={() => toggleFullName(file.id)}
          >
            {file.originalName}
          </button>
        </div>
      </td>
      <td className={`px-6 py-4 ${styles.colExtension}`}>
        <span className="block truncate text-xs font-medium text-on-surface-variant uppercase">.{extensionLabel(file.extension, fileType)}</span>
      </td>
      <td className={`px-6 py-4 text-xs text-on-surface-variant ${styles.colSize}`}><span className="block truncate">{formatStorage(file.fileSize)}</span></td>
      <td className={`px-6 py-4 text-xs text-on-surface-variant ${styles.colUploadedBy}`}>
        <span className="block truncate" title={`${file.uploadedBy.firstName} ${file.uploadedBy.lastName}`}>{file.uploadedBy.firstName} {file.uploadedBy.lastName}</span>
      </td>
      <td className={`px-6 py-4 text-xs text-on-surface-variant ${styles.colDate}`}><span className="block truncate">{formatDate(file.createdAt)}</span></td>
      <td className={`px-6 py-4 ${styles.colStatus}`}>
        <span className={
          file.status === 'ready' ? 'badge-green' :
          file.status === 'processing' ? 'badge-blue' :
          file.status === 'error' ? 'text-[10px] font-bold text-red-600 bg-red-50 px-2 py-1 rounded-full' :
          'badge-orange'
        }>
          {file.status}
        </span>
        {file.status === 'processing' && progressMap[file.id] && (
          <ConversionProgressBar progress={progressMap[file.id]} />
        )}
      </td>
      <td className={`px-6 py-4 text-right ${styles.colActions}`}>
        <div className="flex justify-end gap-1 items-center">
          {canWrite && folders.length > 0 && (
            <div className="relative" ref={folderMenuFileId === file.id ? folderMenuRef : null}>
              <button
                onClick={() => setFolderMenuFileId(prev => prev === file.id ? null : file.id)}
                className="p-2 hover:bg-surface-container rounded text-on-surface-variant transition-colors"
                title="Move to folder"
              >
                <Icon name="folder_open" className="text-lg" />
              </button>
              {folderMenuFileId === file.id && (
                <div className={styles.folderMenu}>
                  <p className={styles.folderMenuLabel}>Move to folder</p>
                  {file.folderId && (
                    <button className={styles.folderMenuItem} onClick={() => assignToFolder(file.id, null)}>
                      <Icon name="remove_circle_outline" style={{ fontSize: 14 }} />
                      Remove from folder
                    </button>
                  )}
                  {folders.map(f => (
                    <button
                      key={f.id}
                      className={`${styles.folderMenuItem} ${file.folderId === f.id ? styles.folderMenuItemActive : ''}`}
                      onClick={() => assignToFolder(file.id, f.id)}
                    >
                      <Icon name="folder" style={{ fontSize: 14 }} />
                      {f.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {onRetryGaussian && file.status === 'error' && (
            <button
              onClick={() => onRetryGaussian(file.id)}
              className="p-2 hover:bg-primary/10 rounded text-primary transition-colors"
              title="Retry training"
            >
              <Icon name="replay" className="text-lg" />
            </button>
          )}
          {arch.chip && (
            <span
              className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-slate-100 text-slate-500 border border-slate-200"
              title={arch.chipTitle}
            >
              {arch.chip}
            </span>
          )}
          {arch.canView && viewerPathFor(file) && (
            <button
              onClick={() => navigate(`/projects/${projectId}/${viewerPathFor(file)}/${file.id}`)}
              className="p-2 hover:bg-primary/10 rounded text-primary transition-colors"
              title="View"
            >
              <Icon name="open_in_new" className="text-lg" />
            </button>
          )}
          {arch.canRequest && onRequestRestore && (
            <button
              onClick={() => onRequestRestore(file)}
              className="p-2 hover:bg-primary/10 rounded text-primary transition-colors"
              title={arch.st === 'archived'
                ? 'Unarchive this file — restores viewing and download (usually 3–5 hours, email when ready)'
                : 'Downloading needs retrieval from the archive (usually 3–5 hours — you\'ll get an email)'}
            >
              <Icon name="unarchive" className="text-lg" />
            </button>
          )}
          {!downloadsExceeded && arch.canDownload && (
            <button
              onClick={() => handleDownload(file)}
              className="p-2 hover:bg-secondary/10 rounded text-secondary transition-colors"
              title="Download"
            >
              <Icon name="download" className="text-lg" />
            </button>
          )}
          {canWrite && (
            <button
              onClick={() => onDeleteFile(file.id)}
              className="p-2 hover:bg-error/10 rounded text-error transition-colors"
              title="Delete"
            >
              <Icon name="delete" className="text-lg" />
            </button>
          )}
        </div>
      </td>
    </tr>
    );
  };

  const headerRow = showHeader ? (
    folderInfo ? (
      <tr key={`folder-hdr-${folderInfo.id}`} className={styles.folderHeaderRow}>
        <td colSpan={7} className={styles.folderHeaderCell}>
          <div className={styles.folderHeaderInner}>
            <button className={styles.folderCollapseBtn} onClick={onToggleCollapse} title={collapsed ? 'Expand' : 'Collapse'}>
              <Icon name="expand_more" style={{ fontSize: 16, transition: 'transform 0.15s', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }} />
            </button>
            <Icon name="folder" style={{ fontSize: 16, color: '#f97316' }} />
            <span className={styles.folderName}>{folderInfo.name}</span>
            <span className={styles.sectionCount}>{total} file{total !== 1 ? 's' : ''}</span>
            {canWrite && (
              <div className={styles.folderActions}>
                <button className={styles.folderActionBtn} title="Rename folder" onClick={onRenameFolder}>
                  <Icon name="edit" style={{ fontSize: 14 }} />
                </button>
                <button
                  className={`${styles.folderActionBtn} ${styles.folderActionBtnDanger}`}
                  title="Delete folder"
                  onClick={onDeleteFolder}
                >
                  <Icon name="delete" style={{ fontSize: 14 }} />
                </button>
              </div>
            )}
          </div>
        </td>
      </tr>
    ) : (
      <tr className={styles.sectionHeaderRow}>
        <td colSpan={7} className={styles.sectionHeaderCell}>
          <div className={styles.folderHeaderInner}>
            <button className={styles.folderCollapseBtn} onClick={onToggleCollapse} title={collapsed ? 'Expand' : 'Collapse'}>
              <Icon name="expand_more" style={{ fontSize: 16, transition: 'transform 0.15s', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }} />
            </button>
            <Icon name="list" style={{ fontSize: 14 }} />
            <span className={styles.folderName} style={{ paddingRight: 15 }}>Ungrouped</span>
            <span className={styles.sectionCount}>{total} file{total !== 1 ? 's' : ''}</span>
          </div>
        </td>
      </tr>
    )
  ) : null;

  if (collapsed && showHeader) {
    return <>{headerRow}</>;
  }

  return (
    <>
      {headerRow}
      {loading && page === 1 ? (
        <tr>
          <td colSpan={7} className="px-6 py-4 text-sm text-on-surface-variant text-center">
            <Icon name="refresh" className="animate-spin mr-1 text-base align-middle" />
            Loading...
          </td>
        </tr>
      ) : error && files.length === 0 ? (
        <tr>
          <td colSpan={7} className={styles.sectionErrorCell}>
            Could not load these files: {error.message}
          </td>
        </tr>
      ) : files.length === 0 ? (
        showHeader ? (
          <tr>
            <td colSpan={7} className={styles.folderEmptyCell}>
              No files {folderInfo ? 'in this folder' : 'ungrouped'}
            </td>
          </tr>
        ) : null
      ) : (
        <>
          {files.map(file => renderFileRow(file))}
          {total > PAGE_LIMIT && <PaginationRow page={page} total={total} onPageChange={setPage} />}
        </>
      )}
    </>
  );
};

// ─── FileListPage ─────────────────────────────────────────────────────────────
const FileListPage = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const fileType = location.pathname.split('/').filter(Boolean).pop();
  const navigate = useNavigate();
  const client = useApolloClient();
  const { user } = useAuth();
  const downloadsExceeded = user?.downloadsExceeded ?? false;

  // Archived files: kick off a Glacier restore; the backend emails the
  // requester when the temp copy is ready (usually 3-5 hours). Stage 1
  // ('raw_archived') retrieves just the original for download; stage 2
  // ('archived') restores everything, viewing included.
  const requestRestore = async (file: PCFile) => {
    const msg = file.archiveState === 'archived'
      ? `Unarchive "${file.originalName}"?\n\nThis restores viewing and download \u2014 usually 3\u20135 hours. You'll get an email when it's ready.`
      : `Request the original of "${file.originalName}" from the archive?\n\nRetrieval usually takes 3\u20135 hours \u2014 you'll get an email when it's ready to download.`;
    if (!window.confirm(msg)) return;
    try {
      const token = localStorage.getItem('geodraft_token') || '';
      const res = await fetch(`/api/files/${file.id}/restore`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Could not start the retrieval');
      client.refetchQueries({ include: ['ProjectFiles'] });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not start the retrieval');
    }
  };

  // Project-level unarchive: one request restores every stage-2 file, one
  // email when the whole project is back.
  const [archivedSections, setArchivedSections] = useState<Record<string, boolean>>({});
  const [unarchiving, setUnarchiving] = useState(false);
  const flagArchivedSection = useCallback((key: string, has: boolean) => {
    setArchivedSections((prev) => (prev[key] === has ? prev : { ...prev, [key]: has }));
  }, []);
  const hasArchivedFiles = Object.values(archivedSections).some(Boolean);

  const requestProjectUnarchive = async () => {
    if (!window.confirm('Unarchive this project?\n\nEvery archived file will be restored \u2014 usually 3\u20135 hours. You\'ll get one email when the project is ready.')) return;
    setUnarchiving(true);
    try {
      const token = localStorage.getItem('geodraft_token') || '';
      const res = await fetch(`/api/projects/${projectId}/unarchive`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Could not start the unarchive');
      if (body.message) window.alert(body.message);
      client.refetchQueries({ include: ['ProjectFiles'] });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not start the unarchive');
    } finally {
      setUnarchiving(false);
    }
  };

  const { data: projectData } = useQuery(PROJECT_QUERY, {
    variables: { id: projectId },
    skip: !projectId,
  });
  const projectName: string | undefined = projectData?.project?.name;
  // Owner, editors and staff with All Projects change files; commenters and
  // viewers only look. Unknown until the project loads.
  const canWrite = canEditProject(projectRoleFor(projectData?.project, user));
  const [actionError, setActionError] = useState('');

  const [search, setSearch] = useState('');
  // A new search sends every section back to its first page. Here, not in the
  // sections: they are mounted again while results load, and would not see the change.
  const resetPages = useResetPageParams();
  useOnChange(resetPages, [search]);
  const handleSearch = (q: string) => setSearch(q);

  const [showUpload, setShowUpload] = useState(false);
  // ?upload=1 (the project page's "Upload 360 video") opens the drop zone at once.
  useEffect(() => {
    if (new URLSearchParams(location.search).get('upload') === '1') setShowUpload(true);
  }, [location.search]);
  const [uploadFolderId, setUploadFolderId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [folderError, setFolderError] = useState('');

  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameFolder, setRenameFolder] = useState<PCFolder | null>(null);
  const [renameName, setRenameName] = useState('');
  const [deleteFolderId, setDeleteFolderId] = useState<string | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const bulkMenuRef = useRef<HTMLDivElement>(null);

  const pageType = (fileType as FilePageType) || '2d';
  const config = PAGE_CONFIG[pageType];
  const documentFormats = useDocumentFormats();

  // Gaussian section extras: monthly quota + generate-from-existing-point-cloud.
  const [showGenerate, setShowGenerate] = useState(false);
  const [generateSelection, setGenerateSelection] = useState<Set<string>>(new Set());
  const [generateError, setGenerateError] = useState('');
  const [generating, setGenerating] = useState(false);
  const { data: quotaData, refetch: refetchQuota } = useQuery(GAUSSIAN_QUOTA_QUERY, {
    skip: pageType !== 'gaussian',
    fetchPolicy: 'cache-and-network',
  });
  const { data: sourceCloudsData } = useQuery(PROJECT_FILES_QUERY, {
    variables: { projectId, type: 'point_cloud', page: 1, limit: 100 },
    skip: !projectId || pageType !== 'gaussian' || !showGenerate,
    fetchPolicy: 'cache-and-network',
  });
  const [generateSplat] = useMutation(GENERATE_GAUSSIAN_SPLAT_MUTATION);

  // Count query — used only for header total and empty-state detection
  const { data: countData, loading: countLoading, error: countError, refetch: refetchCount } = useQuery(PROJECT_FILES_QUERY, {
    variables: { projectId, type: config?.fileType, search: search || undefined, page: 1, limit: 1 },
    skip: !projectId || !config,
    fetchPolicy: 'cache-and-network',
  });

  const { data: foldersData, refetch: refetchFolders } = useQuery(FILE_FOLDERS_QUERY, {
    variables: { projectId, fileType: config?.fileType },
    skip: !projectId || !config,
    fetchPolicy: 'cache-and-network',
  });

  const [deleteFile, { loading: deleting }] = useMutation(DELETE_FILE_MUTATION, {
    onCompleted: () => {
      setDeleteId(null);
      refetchCount();
      client.refetchQueries({ include: ['ProjectFiles'] });
    },
    onError: (err) => setDeleteError(err.message),
  });

  const [createFolder] = useMutation(CREATE_FILE_FOLDER_MUTATION, {
    onCompleted: () => { setShowCreateFolder(false); setNewFolderName(''); refetchFolders(); },
    onError: (err) => setFolderError(err.message),
  });

  const [renameF] = useMutation(RENAME_FILE_FOLDER_MUTATION, {
    onCompleted: () => { setRenameFolder(null); refetchFolders(); },
    onError: (err) => setFolderError(err.message),
  });

  const [deleteFolder] = useMutation(DELETE_FILE_FOLDER_MUTATION, {
    onCompleted: () => {
      setDeleteFolderId(null);
      refetchFolders();
      refetchCount();
      client.refetchQueries({ include: ['ProjectFiles'] });
    },
    onError: (err) => setFolderError(err.message),
  });

  const openDeleteFile = (id: string | null) => { setDeleteError(''); setDeleteId(id); };
  const closeFolderDialogs = () => {
    setFolderError('');
    setShowCreateFolder(false);
    setNewFolderName('');
    setRenameFolder(null);
    setDeleteFolderId(null);
  };
  const submitCreateFolder = () => {
    setFolderError('');
    createFolder({ variables: { projectId: projectId!, name: newFolderName, fileType: config.fileType } });
  };
  const submitRenameFolder = () => {
    setFolderError('');
    renameF({ variables: { id: renameFolder!.id, name: renameName } });
  };

  const [setFileFolderMutation] = useMutation(SET_FILE_FOLDER_MUTATION);

  const handleSectionMutationComplete = () => {
    refetchCount();
    client.refetchQueries({ include: ['ProjectFiles'] });
  };

  const assignBulkToFolder = async (folderId: string | null) => {
    setBulkMenuOpen(false);
    try {
      await Promise.all(
        Array.from(selectedFiles).map(fileId =>
          setFileFolderMutation({ variables: { fileId, folderId } })
        )
      );
      setSelectedFiles(new Set());
    } catch (err) {
      setActionError(`Could not move the files: ${errorMessage(err, 'unknown error')}`);
    }
    handleSectionMutationComplete();
  };

  const totalFiles = countData?.projectFiles?.total ?? 0;
  const folders: PCFolder[] = foldersData?.fileFolders ?? [];

  const toggleSection = (key: string) => {
    setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => { setSelectedFiles(new Set()); }, [search]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(e.target as Node)) {
        setBulkMenuOpen(false);
      }
    };
    if (bulkMenuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [bulkMenuOpen]);

  const toggleSelect = (fileId: string) => {
    setSelectedFiles(prev => {
      const next = new Set(prev);
      next.has(fileId) ? next.delete(fileId) : next.add(fileId);
      return next;
    });
  };

  if (!config) return <div>Invalid page type</div>;

  // LGSX upload is a per-user permission (admin -> Users -> Edit permissions).
  // Without it .lgsx is absent from the supported point-cloud formats entirely
  // (the backend rejects it at /api/upload/prepare as well).
  const canUploadLgsx = user?.permissions?.lgsxUpload || isSuperAdmin(user);

  // The whole Gaussian Splats section is permission-gated (backend enforces
  // too). gaussianVisible = see the section + upload trained .ply files;
  // gaussianGenerate additionally allows scan uploads + generate-from-cloud.
  const isGaussianPage = pageType === 'gaussian';
  const access = accessOf(user);
  const gaussianAllowed = access.splat;
  const gaussianGenerateAllowed = user?.permissions?.gaussianGenerate || isSuperAdmin(user);
  const sectionExtensions = pageType === 'documents' ? documentFormats.map((f) => f.extension) : config.allowedExtensions;
  const allowedExtensions = sectionExtensions.filter((e) =>
    (e !== 'lgsx' || canUploadLgsx)
    && (!isGaussianPage || gaussianGenerateAllowed || e === 'ply'));
  if (isGaussianPage && !gaussianAllowed) {
    return (
      <DashboardLayout sidebarContext="project" projectId={projectId}>
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Icon name="blur_on" className="text-5xl text-outline mb-4" />
          <h3 className="text-lg font-bold text-on-surface mb-2">Gaussian splats are not enabled</h3>
          <p className="text-sm text-on-surface-variant">Ask your administrator to enable Gaussian splats for your account.</p>
        </div>
      </DashboardLayout>
    );
  }

  // Orthomosaic mirrors the gaussian gate: one permission covers seeing the
  // section, uploading and viewing (backend enforces all three too).
  if (pageType === 'orthomosaic' && !access.ortho) {
    return (
      <DashboardLayout sidebarContext="project" projectId={projectId}>
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Icon name="satellite_alt" className="text-5xl text-outline mb-4" />
          <h3 className="text-lg font-bold text-on-surface mb-2">Orthomosaics are not enabled</h3>
          <p className="text-sm text-on-surface-variant">Ask your administrator to enable orthomosaic images for your account.</p>
        </div>
      </DashboardLayout>
    );
  }

  if (pageType === 'video360' && !access.video360) {
    return (
      <DashboardLayout sidebarContext="project" projectId={projectId}>
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Icon name="panorama_photosphere" className="text-5xl text-outline mb-4" />
          <h3 className="text-lg font-bold text-on-surface mb-2">360 videos are not enabled</h3>
          <p className="text-sm text-on-surface-variant">Ask your administrator to enable 360 videos for your account.</p>
        </div>
      </DashboardLayout>
    );
  }

  const runGenerate = async () => {
    setGenerating(true);
    setGenerateError('');
    try {
      for (const id of Array.from(generateSelection)) {
        await generateSplat({ variables: { fileId: id } });
      }
      setShowGenerate(false);
      setGenerateSelection(new Set());
      refetchCount();
      refetchQuota();
      client.refetchQueries({ include: ['ProjectFiles'] });
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : 'Generation failed');
      refetchQuota();
    } finally {
      setGenerating(false);
    }
  };

  const retryGaussian = async (fileId: string) => {
    try {
      await generateSplat({ variables: { fileId } });
      refetchQuota();
      client.refetchQueries({ include: ['ProjectFiles'] });
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Retry failed');
    }
  };

  const sharedSectionProps = {
    projectId: projectId!,
    fileType: config.fileType,
    search,
    folders,
    config,
    documentFormats,
    selectedFiles,
    onToggleSelect: toggleSelect,
    onDeleteFile: openDeleteFile,
    onMutationComplete: handleSectionMutationComplete,
    navigate,
    downloadsExceeded,
    onRetryGaussian: canWrite && isGaussianPage && gaussianGenerateAllowed ? retryGaussian : undefined,
    onRequestRestore: requestRestore,
    onArchivedFlag: flagArchivedSection,
    canWrite,
    onActionError: setActionError,
  };

  return (
    <DashboardLayout
      sidebarContext="project"
      projectId={projectId}
      onSearch={handleSearch}
      searchPlaceholder={`Search ${config.title.toLowerCase()}...`}
      topBarActions={canWrite ? (
        <button
          onClick={() => setShowUpload(true)}
          className="btn-primary px-4 py-2 rounded-lg text-sm bg-gradient-to-r from-primary to-primary-container"
        >
          <Icon name="upload" className="text-sm" />
          Upload
        </button>
      ) : undefined}
    >
      <div className="space-y-6">
        <div className={styles.pageHeader}>
          <div>
            <div className="flex items-center gap-2 text-xs text-on-surface-variant mb-2">
              <button onClick={() => navigate('/projects')} className="hover:text-primary transition-colors">Projects</button>
              <Icon name="chevron_right" className="text-sm" />
              <button
                onClick={() => navigate(`/projects/${projectId}`)}
                className="hover:text-primary transition-colors"
                title={projectName}
              >
                {projectName ? truncateName(projectName) : 'Project'}
              </button>
              <Icon name="chevron_right" className="text-sm" />
              <span>{config.title}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className={`p-2 ${config.color} rounded-lg w-fit`}>
                <Icon name={config.icon} />
              </div>
              <div>
                <h2 className="text-2xl font-bold tracking-tight text-on-surface font-headline">{config.title}</h2>
                <p className="text-on-surface-variant text-sm">
              {totalFiles} files · {allowedExtensions.map((e) => `.${extensionLabel(e, config.fileType)}`).join(', ')}
              {isGaussianPage && gaussianGenerateAllowed && quotaData?.gaussianQuota && (
                <span className="ml-2 text-xs font-semibold text-indigo-600">
                  · {quotaData.gaussianQuota.used}{quotaData.gaussianQuota.limit != null ? ` / ${quotaData.gaussianQuota.limit}` : ''} generations this month
                </span>
              )}
            </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasArchivedFiles && (
              <button
                onClick={requestProjectUnarchive}
                disabled={unarchiving}
                className="px-4 py-2.5 rounded-lg text-sm font-medium border border-outline-variant text-on-surface-variant bg-surface hover:bg-surface-container transition-colors flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
                title="Restore every archived file in this project (usually 3–5 hours — one email when ready)"
              >
                <Icon name="unarchive" className="text-sm" />
                {unarchiving ? 'Requesting…' : 'Unarchive Project'}
              </button>
            )}
            {canWrite && (
              <>
                <button
                  onClick={() => { setFolderError(''); setShowCreateFolder(true); }}
                  className="px-4 py-2.5 rounded-lg text-sm font-medium border border-outline-variant text-on-surface-variant bg-surface hover:bg-surface-container transition-colors flex items-center gap-1.5"
                >
                  <Icon name="create_new_folder" className="text-sm" />
                  New Folder
                </button>
                {isGaussianPage && gaussianGenerateAllowed && (
                  <button
                    onClick={() => { setGenerateError(''); setGenerateSelection(new Set()); setShowGenerate(true); }}
                    className="px-4 py-2.5 rounded-lg text-sm font-medium border border-outline-variant text-on-surface bg-surface hover:bg-surface-container transition-colors flex items-center gap-1.5"
                  >
                    <Icon name="grain" className="text-sm" />
                    From Point Cloud
                  </button>
                )}
                <button
                  onClick={() => setShowUpload(true)}
                  className="btn-primary px-5 py-2.5 rounded-lg text-sm bg-gradient-to-r from-primary to-primary-container"
                >
                  <Icon name="upload" className="text-sm" />
                  Upload Files
                </button>
              </>
            )}
          </div>
        </div>

        {actionError && (
          <div className={styles.errorBanner} role="alert">
            <Icon name="error" className="text-base" />
            <span className="flex-1">{actionError}</span>
            <button className={styles.errorBannerClose} onClick={() => setActionError('')} title="Dismiss">
              <Icon name="close" className="text-base" />
            </button>
          </div>
        )}

        {countError && !countData ? (
          <div className="bg-white rounded-xl p-12 text-center" role="alert">
            <Icon name="error" className="text-4xl text-error mb-3 block" />
            <h3 className="text-lg font-bold text-on-surface mb-2">Could not load {config.title.toLowerCase()}</h3>
            <p className="text-sm text-on-surface-variant mb-6">{countError.message}</p>
            <button
              onClick={() => { refetchCount(); refetchFolders(); client.refetchQueries({ include: ['ProjectFiles'] }); }}
              className="px-5 py-2.5 rounded-lg text-sm font-semibold border border-outline-variant text-on-surface bg-surface hover:bg-surface-container transition-colors"
            >
              Try again
            </button>
          </div>
        ) : countLoading && totalFiles === 0 && folders.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-on-surface-variant">
            <Icon name="refresh" className="animate-spin mr-2" />
            Loading files...
          </div>
        ) : search && totalFiles === 0 && folders.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <Icon name="search_off" className="text-4xl text-outline mb-3 block" />
            <h3 className="text-lg font-bold text-on-surface mb-2">No files match &ldquo;{search}&rdquo;</h3>
            <p className="text-sm text-on-surface-variant">Try a different name, or clear the search.</p>
          </div>
        ) : totalFiles === 0 && folders.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <div className={`w-16 h-16 ${config.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
              <Icon name={config.icon} className="text-3xl" />
            </div>
            <h3 className="text-lg font-bold text-on-surface mb-2">No {config.title.toLowerCase()} yet</h3>
            <p className="text-sm text-on-surface-variant mb-6">
              {!canWrite
                ? 'Files added to this project will appear here.'
                : isGaussianPage
                ? (gaussianGenerateAllowed
                  ? 'Generate a photoreal scene from an already-converted point cloud, or upload a new scan'
                  : 'Upload a trained Gaussian splat (.ply) generated elsewhere')
                : `Upload ${allowedExtensions.map((e) => `.${extensionLabel(e, config.fileType)}`).join(', ')} files to get started`}
            </p>
            {canWrite && <div className="flex items-center justify-center gap-3">
              {isGaussianPage && gaussianGenerateAllowed && (
                <button
                  onClick={() => { setGenerateError(''); setGenerateSelection(new Set()); setShowGenerate(true); }}
                  className="px-5 py-2.5 rounded-lg text-sm font-semibold border border-outline-variant text-on-surface bg-surface hover:bg-surface-container transition-colors flex items-center gap-1.5"
                >
                  <Icon name="grain" className="text-sm" />
                  From Point Cloud
                </button>
              )}
              <button
                onClick={() => setShowUpload(true)}
                className="btn-primary px-6 py-2.5 rounded-lg text-sm bg-gradient-to-r from-primary to-primary-container"
              >
                <Icon name="upload" className="text-sm" />
                Upload Files
              </button>
            </div>}
          </div>
        ) : (
          <div className="bg-white rounded-xl overflow-hidden">
            {canWrite && selectedFiles.size > 0 && (
              <div className={styles.bulkBar}>
                <span className={styles.bulkCount}>
                  {selectedFiles.size} file{selectedFiles.size !== 1 ? 's' : ''} selected
                </span>
                <div className={styles.bulkActions}>
                  {folders.length > 0 && (
                    <div className="relative" ref={bulkMenuRef}>
                      <button className={styles.bulkBtn} onClick={() => setBulkMenuOpen(o => !o)}>
                        <Icon name="folder_open" style={{ fontSize: 15 }} />
                        Move to folder
                        <Icon name="expand_more" style={{ fontSize: 14 }} />
                      </button>
                      {bulkMenuOpen && (
                        <div className={styles.folderMenu}>
                          <p className={styles.folderMenuLabel}>Assign to folder</p>
                          <button className={styles.folderMenuItem} onClick={() => assignBulkToFolder(null)}>
                            <Icon name="remove_circle_outline" style={{ fontSize: 14 }} />
                            Remove from folder
                          </button>
                          {folders.map(f => (
                            <button key={f.id} className={styles.folderMenuItem} onClick={() => assignBulkToFolder(f.id)}>
                              <Icon name="folder" style={{ fontSize: 14 }} />
                              {f.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <button className={styles.bulkClearBtn} onClick={() => setSelectedFiles(new Set())}>
                    Clear selection
                  </button>
                </div>
              </div>
            )}
            <div className={styles.tableWrap}>
              <table className={`text-left border-collapse ${styles.fileTable}`}>
                <thead>
                  <tr className="bg-surface-container-low">
                    {FILE_COLUMNS.map((c) => (
                      <th
                        key={c.key}
                        className={`table-header ${c.cell} ${c.key === 'actions' ? 'text-right' : ''}`}
                      >
                        {c.key === 'name' ? (
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              className={styles.checkbox}
                              checked={false}
                              onChange={() => setSelectedFiles(new Set())}
                              style={{ visibility: selectedFiles.size > 0 ? 'visible' : 'hidden' }}
                              title="Clear selection"
                            />
                            File Name
                          </div>
                        ) : c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/10">
                  {folders.length === 0 ? (
                    <FileSection
                      {...sharedSectionProps}
                      folderId={undefined}
                      folderInfo={null}
                      showHeader={false}
                      collapsed={false}
                      onToggleCollapse={() => {}}
                    />
                  ) : (
                    <>
                      <FileSection
                        {...sharedSectionProps}
                        folderId={null}
                        folderInfo={null}
                        showHeader={true}
                        collapsed={!!collapsedSections['__ungrouped__']}
                        onToggleCollapse={() => toggleSection('__ungrouped__')}
                      />
                      {folders.map(folder => (
                        <FileSection
                          key={folder.id}
                          {...sharedSectionProps}
                          folderId={folder.id}
                          folderInfo={folder}
                          showHeader={true}
                          collapsed={!!collapsedSections[folder.id]}
                          onToggleCollapse={() => toggleSection(folder.id)}
                          onRenameFolder={() => { setFolderError(''); setRenameFolder(folder); setRenameName(folder.name); }}
                          onDeleteFolder={() => { setFolderError(''); setDeleteFolderId(folder.id); }}
                        />
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      <Modal
        isOpen={showUpload && canWrite}
        onClose={() => { setShowUpload(false); setUploadFolderId(null); }}
        title={`Upload ${config.title}`}
        closeOnOverlayClick={false}
      >
        <div className="space-y-4">
          <p className="text-sm text-on-surface-variant">
            Upload {allowedExtensions.map((e) => `.${extensionLabel(e, config.fileType).toUpperCase()}`).join(', ')} files to this project.
          </p>
          {folders.length > 0 && (
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-on-surface-variant flex items-center gap-1.5">
                <Icon name="folder" className="text-sm" />
                Destination folder
              </label>
              <select
                className="w-full px-3 py-2 border border-outline-variant rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                value={uploadFolderId ?? ''}
                onChange={(e) => setUploadFolderId(e.target.value || null)}
              >
                <option value="">Ungrouped</option>
                {folders.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          )}
          <FileUploadZone
            projectId={projectId!}
            allowedExtensions={allowedExtensions}
            folderId={uploadFolderId}
            sectionHint={config.fileType}
            onUploadComplete={() => {
              setShowUpload(false);
              setUploadFolderId(null);
              refetchCount();
              client.refetchQueries({ include: ['ProjectFiles'] });
            }}
          />
        </div>
      </Modal>

      {/* Generate splats from existing point clouds */}
      <Modal
        isOpen={showGenerate}
        onClose={() => setShowGenerate(false)}
        title="Generate from point cloud"
        footer={
          <>
            <button onClick={() => setShowGenerate(false)} className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors">
              Cancel
            </button>
            <button
              onClick={runGenerate}
              disabled={generating || generateSelection.size === 0}
              className="btn-primary px-5 py-2 rounded-lg text-sm disabled:opacity-50"
            >
              {generating ? 'Starting…' : `Generate${generateSelection.size > 1 ? ` (${generateSelection.size})` : ''}`}
            </button>
          </>
        }
      >
        <p className="text-sm text-on-surface-variant mb-4">
          Reuses point clouds already uploaded to this project — no second upload. Each selected file
          uses one Gaussian splat generation from your monthly plan and trains for roughly an hour.
          Files without imagery (no panoramas) can&apos;t be used.
        </p>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {(sourceCloudsData?.projectFiles?.files ?? []).map((f: PCFile & { panoramaCount?: number }) => {
            const eligible = f.status === 'ready' && (f.panoramaCount ?? 0) > 0;
            const checked = generateSelection.has(f.id);
            return (
              <label
                key={f.id}
                className={`flex items-center gap-3 p-3 rounded-lg border border-outline-variant transition-colors ${
                  eligible ? 'cursor-pointer hover:bg-surface-container' : 'opacity-50 cursor-not-allowed'
                }`}
              >
                <input
                  type="checkbox"
                  className="accent-primary w-4 h-4 flex-shrink-0"
                  disabled={!eligible}
                  checked={checked}
                  onChange={() => {
                    setGenerateSelection((prev) => {
                      const next = new Set(prev);
                      if (next.has(f.id)) next.delete(f.id);
                      else next.add(f.id);
                      return next;
                    });
                  }}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate" title={f.originalName}>{f.originalName}</p>
                  <p className="text-xs text-on-surface-variant">
                    {formatStorage(f.fileSize)}
                    {eligible
                      ? ` · ${f.panoramaCount} panorama${(f.panoramaCount ?? 0) !== 1 ? 's' : ''}`
                      : f.status !== 'ready' ? ' · still converting' : ' · no imagery'}
                  </p>
                </div>
              </label>
            );
          })}
          {sourceCloudsData && (sourceCloudsData.projectFiles?.files ?? []).length === 0 && (
            <p className="text-sm text-on-surface-variant text-center py-6">No point cloud files in this project yet.</p>
          )}
        </div>
        {generateError && <p className="text-sm text-error mt-3">{generateError}</p>}
      </Modal>

      {/* Delete File Modal */}
      <Modal
        isOpen={!!deleteId}
        onClose={() => openDeleteFile(null)}
        title="Delete File"
        footer={
          <>
            <button onClick={() => openDeleteFile(null)} className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors">Cancel</button>
            <button
              onClick={() => { setDeleteError(''); deleteFile({ variables: { id: deleteId } }); }}
              disabled={deleting}
              className="px-5 py-2 rounded-lg text-sm font-medium bg-error text-white hover:opacity-90 disabled:opacity-50"
            >
              Delete
            </button>
          </>
        }
      >
        <p className="text-sm text-on-surface-variant">Are you sure you want to delete this file? This action cannot be undone.</p>
        {deleteError && <p className="text-sm text-error mt-3" role="alert">{deleteError}</p>}
      </Modal>

      {/* Create Folder Modal */}
      <Modal
        isOpen={showCreateFolder}
        onClose={closeFolderDialogs}
        title="New Folder"
        footer={
          <>
            <button onClick={closeFolderDialogs} className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors">Cancel</button>
            <button
              onClick={submitCreateFolder}
              disabled={!newFolderName.trim()}
              className="px-5 py-2 rounded-lg text-sm font-medium bg-primary text-white hover:opacity-90 disabled:opacity-40"
            >
              Create
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-on-surface-variant">Give this folder a name to group related files together.</p>
          <input
            type="text"
            className="w-full px-3 py-2 border border-outline-variant rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            placeholder="Folder name"
            value={newFolderName}
            onChange={e => setNewFolderName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && newFolderName.trim()) submitCreateFolder(); }}
            autoFocus
          />
          {folderError && <p className="text-sm text-error" role="alert">{folderError}</p>}
        </div>
      </Modal>

      {/* Rename Folder Modal */}
      <Modal
        isOpen={!!renameFolder}
        onClose={closeFolderDialogs}
        title="Rename Folder"
        footer={
          <>
            <button onClick={closeFolderDialogs} className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors">Cancel</button>
            <button
              onClick={submitRenameFolder}
              disabled={!renameName.trim()}
              className="px-5 py-2 rounded-lg text-sm font-medium bg-primary text-white hover:opacity-90 disabled:opacity-40"
            >
              Rename
            </button>
          </>
        }
      >
        <input
          type="text"
          className="w-full px-3 py-2 border border-outline-variant rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          value={renameName}
          onChange={e => setRenameName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && renameName.trim()) submitRenameFolder(); }}
          autoFocus
        />
        {folderError && <p className="text-sm text-error mt-3" role="alert">{folderError}</p>}
      </Modal>

      {/* Delete Folder Modal */}
      <Modal
        isOpen={!!deleteFolderId}
        onClose={closeFolderDialogs}
        title="Delete Folder"
        footer={
          <>
            <button onClick={closeFolderDialogs} className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors">Cancel</button>
            <button onClick={() => { setFolderError(''); deleteFolder({ variables: { id: deleteFolderId } }); }} className="px-5 py-2 rounded-lg text-sm font-medium bg-error text-white hover:opacity-90">Delete</button>
          </>
        }
      >
        <p className="text-sm text-on-surface-variant">The folder will be deleted. Files inside it will become ungrouped.</p>
        {folderError && <p className="text-sm text-error mt-3" role="alert">{folderError}</p>}
      </Modal>
    </DashboardLayout>
  );
};

export default FileListPage;
